import Phaser from 'phaser'
import { Player } from '../entities/Player'
import { VirtualJoystick } from '../controls/VirtualJoystick'
import { SpawnSystem } from '../systems/SpawnSystem'
import { CombatSystem } from '../systems/CombatSystem'
import { StatsSystem } from '../systems/StatsSystem'
import { SpellSystem } from '../systems/SpellSystem'
import { LootSystem } from '../systems/LootSystem'
import { InventorySystem } from '../systems/InventorySystem'
import { CombatInput } from '../controls/CombatInput'
import { TargetIndicator } from '../ui/TargetIndicator'
import { DamageNumbers } from '../ui/DamageNumbers'
import { TILES } from '../types/GameTypes'
import { TownMap, MapData } from '../maps/TownMap'

type MapType = 'town' | 'wilderness'

interface Decoration {
  x: number
  y: number
  type: string
}

interface Obstacle {
  x: number
  y: number
  type: string
}

interface WASD {
  up: Phaser.Input.Keyboard.Key
  down: Phaser.Input.Keyboard.Key
  left: Phaser.Input.Keyboard.Key
  right: Phaser.Input.Keyboard.Key
}

export class GameScene extends Phaser.Scene {
  private tileSize: number = 32
  private mapWidth: number = 30  // Town is 30x30
  private mapHeight: number = 30

  // Map type - 'town' disables monster spawns
  private currentMapType: MapType = 'town'

  public player!: Player
  private joystick!: VirtualJoystick
  private cursors!: Phaser.Types.Input.Keyboard.CursorKeys
  private wasd!: WASD

  private map!: Phaser.Tilemaps.Tilemap
  private decorationLayer!: Phaser.GameObjects.Group
  private obstacleLayer!: Phaser.Physics.Arcade.StaticGroup

  // Combat and creature systems
  private creatureGroup!: Phaser.GameObjects.Group
  private spawnSystem!: SpawnSystem
  public combatSystem!: CombatSystem
  public spellSystem!: SpellSystem
  private statsSystem!: StatsSystem
  // Combat input handler (stored for cleanup, used via event listeners)
  private combatInput!: CombatInput
  private targetIndicator!: TargetIndicator
  private damageNumbers!: DamageNumbers

  // Loot and inventory systems
  public inventorySystem!: InventorySystem
  public lootSystem!: LootSystem

  // Map data for spawn system
  private groundData!: number[][]

  // Spawn system enabled flag
  private spawnsEnabled: boolean = false

  constructor() {
    super({ key: 'GameScene' })
  }

  create(): void {
    // Generate the map
    this.createMap()

    // Create player
    this.createPlayer()

    // Set up creature and combat systems
    this.setupCreatureSystems()

    // Set up controls
    this.setupControls()

    // Set up camera
    this.setupCamera()

    // Launch UI scene
    this.scene.launch('UIScene')

    // Collision between player and obstacles
    this.physics.add.collider(this.player, this.obstacleLayer)
  }

  private createMap(): void {
    let mapData: MapData

    if (this.currentMapType === 'town') {
      // Use TownMap for peaceful town experience
      const townMap = new TownMap({
        tileSize: this.tileSize,
        mapWidth: this.mapWidth,
        mapHeight: this.mapHeight
      })
      mapData = townMap.generate()
      this.spawnsEnabled = false
    } else {
      // Use wilderness map (original generation)
      this.mapWidth = 50
      this.mapHeight = 50
      mapData = this.generateMapData()
      this.spawnsEnabled = true
    }

    this.groundData = mapData.ground

    // Create tilemap
    this.map = this.make.tilemap({
      data: mapData.ground,
      tileWidth: this.tileSize,
      tileHeight: this.tileSize
    })

    // Add tileset
    const tileset = this.map.addTilesetImage('tiles', 'tiles', this.tileSize, this.tileSize, 0, 0)

    if (tileset) {
      // Create ground layer
      this.map.createLayer(0, tileset, 0, 0)
    }

    // Create decoration layer
    this.decorationLayer = this.add.group()

    // Overlay custom tile sprites on the plaza (town only)
    if (this.currentMapType === 'town') {
      this.overlayCustomTiles(mapData.ground)
    }

    this.addDecorations(mapData.decorations)

    // Create obstacle layer for collision
    this.obstacleLayer = this.physics.add.staticGroup()
    this.addObstacles(mapData.obstacles)

    // Set world bounds
    this.physics.world.setBounds(0, 0, this.mapWidth * this.tileSize, this.mapHeight * this.tileSize)
  }

  private generateMapData(): MapData {
    const ground: number[][] = []
    const decorations: Decoration[] = []
    const obstacles: Obstacle[] = []

    // Generate simple terrain - mostly grass with some dirt
    for (let y = 0; y < this.mapHeight; y++) {
      const row: number[] = []
      for (let x = 0; x < this.mapWidth; x++) {
        // Simple grass variation
        const noise = this.simpleNoise(x, y, 0.1)

        let tile: number
        if (noise < -0.2) {
          tile = TILES.GRASS1 + Math.floor(Math.random() * 3)
          if (Math.random() < 0.08) tile = TILES.GRASS_FLOWER
        } else if (noise < 0.3) {
          tile = Math.random() < 0.7 ? TILES.GRASS2 : TILES.DIRT1
        } else {
          tile = TILES.DIRT1 + Math.floor(Math.random() * 2)
        }

        row.push(tile)
      }
      ground.push(row)
    }

    // Add paths connecting areas
    this.generatePaths(ground)

    // Add town area with buildings and decorations
    this.generateSimpleTown(ground, obstacles)

    // Add trees outside town area
    for (let y = 5; y < this.mapHeight - 5; y += 4) {
      for (let x = 5; x < this.mapWidth - 5; x += 4) {
        // Skip town area (22-34, 22-34)
        if (x >= 20 && x <= 36 && y >= 20 && y <= 36) continue
        // Skip path areas
        if ((y >= 26 && y <= 29) || (x >= 26 && x <= 29)) continue

        // Random chance to place tree
        if (Math.random() < 0.6) {
          decorations.push({
            x: x * this.tileSize + this.tileSize / 2,
            y: y * this.tileSize + this.tileSize / 2,
            type: 'tree'
          })
        }
      }
    }

    return { ground, decorations, obstacles }
  }

  private generateSimpleTown(
    ground: number[][],
    obstacles: { x: number; y: number; type: string }[]
  ): void {
    const townX = 22
    const townY = 22
    const townSize = 12

    // Base cobblestone floor for entire town
    for (let y = townY; y < townY + townSize; y++) {
      for (let x = townX; x < townX + townSize; x++) {
        ground[y][x] = TILES.COBBLE
      }
    }

    // Central well (collision obstacle)
    obstacles.push({ x: 27, y: 27, type: 'well' })

    // Decorative objects with collision
    obstacles.push({ x: 24, y: 25, type: 'barrel' })
    obstacles.push({ x: 31, y: 25, type: 'crate' })
    obstacles.push({ x: 24, y: 29, type: 'lamp' })
    obstacles.push({ x: 31, y: 29, type: 'lamp' })
  }

  private simpleNoise(x: number, y: number, scale: number): number {
    // Simple pseudo-random noise
    const n = Math.sin(x * scale * 12.9898 + y * scale * 78.233) * 43758.5453
    return (n - Math.floor(n)) * 2 - 1
  }

  private generatePaths(ground: number[][]): void {
    // Main road from edge to town
    for (let x = 0; x < this.mapWidth; x++) {
      const y = 27
      ground[y][x] = TILES.STONE_PATH
      ground[y + 1][x] = TILES.STONE_PATH
    }

    for (let y = 0; y < this.mapHeight; y++) {
      const x = 27
      ground[y][x] = TILES.STONE_PATH
      ground[y][x + 1] = TILES.STONE_PATH
    }
  }

  /**
   * Overlay custom tile sprites on plaza and grass areas
   */
  private overlayCustomTiles(ground: number[][]): void {
    const centerX = Math.floor(this.mapWidth / 2)
    const centerY = Math.floor(this.mapHeight / 2)
    const plazaRadius = 6

    for (let y = 0; y < this.mapHeight; y++) {
      for (let x = 0; x < this.mapWidth; x++) {
        const tileValue = ground[y][x]
        const pixelX = x * this.tileSize + this.tileSize / 2
        const pixelY = y * this.tileSize + this.tileSize / 2

        // Check if this is a cobblestone/plaza tile
        if (tileValue === TILES.COBBLE || tileValue === TILES.STONE_PATH) {
          // Use cobblestone tile for plaza area
          const inPlaza = Math.abs(x - centerX) <= plazaRadius && Math.abs(y - centerY) <= plazaRadius
          if (inPlaza) {
            const tile = this.add.sprite(pixelX, pixelY, 'tile-cobblestone')
            tile.setDisplaySize(this.tileSize, this.tileSize)
            tile.setDepth(0)
          }
        }
      }
    }
  }

  private addDecorations(decorations: Decoration[]): void {
    const DECORATION_TILES = {
      TREE1: 16, TREE2: 17, BUSH: 18, ROCK: 19,
      WELL: 29, SIGNPOST: 30, LAMP: 31
    }

    decorations.forEach(dec => {
      // Use loaded assets for trees
      if (dec.type === 'tree') {
        const treeSprite = this.add.sprite(dec.x, dec.y, 'asset-tree')
        treeSprite.setDisplaySize(this.tileSize, this.tileSize)
        treeSprite.setDepth(dec.y)
        this.decorationLayer.add(treeSprite)
        return
      }

      if (dec.type === 'tree-variation') {
        const treeSprite = this.add.sprite(dec.x, dec.y, 'asset-tree-variation')
        treeSprite.setDisplaySize(this.tileSize, this.tileSize)
        treeSprite.setDepth(dec.y)
        this.decorationLayer.add(treeSprite)
        return
      }

      // Use loaded asset for torches
      if (dec.type === 'torch') {
        const torchSprite = this.add.sprite(dec.x, dec.y, 'asset-torch')
        torchSprite.setDisplaySize(this.tileSize, this.tileSize)
        torchSprite.setDepth(dec.y)
        this.decorationLayer.add(torchSprite)
        return
      }

      // Use loaded asset for barrels (using multiple-objects.png)
      if (dec.type === 'barrel') {
        const barrelSprite = this.add.sprite(dec.x, dec.y, 'asset-barrel')
        barrelSprite.setDisplaySize(this.tileSize, this.tileSize)
        barrelSprite.setDepth(dec.y)
        this.decorationLayer.add(barrelSprite)
        return
      }

      let frame: number
      switch (dec.type) {
        case 'rock':
          frame = DECORATION_TILES.ROCK
          break
        case 'bush':
          frame = DECORATION_TILES.BUSH
          break
        case 'well':
          frame = DECORATION_TILES.WELL
          break
        case 'signpost':
          frame = DECORATION_TILES.SIGNPOST
          break
        case 'lamp':
          frame = DECORATION_TILES.LAMP
          break
        default:
          frame = DECORATION_TILES.BUSH
      }

      const sprite = this.add.sprite(dec.x, dec.y, 'tiles', frame)
      sprite.setDisplaySize(this.tileSize, this.tileSize)
      sprite.setDepth(dec.y) // Y-sorting for proper overlap
      this.decorationLayer.add(sprite)
    })
  }

  private addObstacles(obstacles: Obstacle[]): void {
    obstacles.forEach(obs => {
      const x = obs.x * this.tileSize + this.tileSize / 2
      const y = obs.y * this.tileSize + this.tileSize / 2

      // Create collision box
      const collider = this.add.rectangle(x, y, this.tileSize - 4, this.tileSize - 4)
      this.physics.add.existing(collider, true)
      this.obstacleLayer.add(collider)

      // Add visual sprite for non-building obstacles
      if (obs.type === 'building') {
        // Buildings are collision-only, no visual (floor tiles show through)
        return
      }

      if (obs.type === 'crate') {
        const sprite = this.add.sprite(x, y, 'asset-crate')
        sprite.setDisplaySize(this.tileSize, this.tileSize)
        sprite.setDepth(y)
        this.decorationLayer.add(sprite)
      } else if (obs.type === 'barrel') {
        const sprite = this.add.sprite(x, y, 'asset-barrel')
        sprite.setDisplaySize(this.tileSize, this.tileSize)
        sprite.setDepth(y)
        this.decorationLayer.add(sprite)
      } else if (obs.type === 'well') {
        // Use well asset
        const sprite = this.add.sprite(x, y, 'asset-well')
        sprite.setDisplaySize(this.tileSize, this.tileSize)
        sprite.setDepth(y)
        this.decorationLayer.add(sprite)
      } else if (obs.type === 'lamp') {
        // Use torch asset for lamps
        const sprite = this.add.sprite(x, y, 'asset-torch')
        sprite.setDisplaySize(this.tileSize, this.tileSize)
        sprite.setDepth(y)
        this.decorationLayer.add(sprite)
      }
    })
  }

  private createPlayer(): void {
    let spawnX: number
    let spawnY: number

    if (this.currentMapType === 'town') {
      // Spawn in center of town (slightly below well)
      spawnX = Math.floor(this.mapWidth / 2) * this.tileSize + this.tileSize / 2
      spawnY = Math.floor(this.mapHeight / 2) * this.tileSize + this.tileSize * 1.5
    } else {
      // Original spawn position for wilderness
      spawnX = 27 * this.tileSize + this.tileSize / 2
      spawnY = 28 * this.tileSize + this.tileSize / 2
    }

    this.player = new Player(this, spawnX, spawnY)
  }

  private setupCreatureSystems(): void {
    // Create creature group
    this.creatureGroup = this.add.group()

    // Only create spawn system if spawns are enabled (not in town)
    if (this.spawnsEnabled) {
      this.spawnSystem = new SpawnSystem(
        this,
        this.player,
        this.groundData,
        this.creatureGroup
      )
    }

    // Create combat system (pass null spawn system for town)
    this.combatSystem = new CombatSystem(this, this.player, this.spawnsEnabled ? this.spawnSystem : null)

    // Create spell system
    this.spellSystem = new SpellSystem(this, this.player, this.combatSystem)

    // Create stats system
    this.statsSystem = new StatsSystem(this, this.player)

    // Create inventory system
    this.inventorySystem = new InventorySystem(this, this.player)

    // Create loot system
    this.lootSystem = new LootSystem(this, this.player, this.inventorySystem)

    // Create combat input handler
    this.combatInput = new CombatInput(this, this.combatSystem, this.cameras.main)

    // Create target indicator
    this.targetIndicator = new TargetIndicator(this)

    // Create damage numbers
    this.damageNumbers = new DamageNumbers(this)
  }

  private setupControls(): void {
    // Virtual joystick for mobile
    this.joystick = new VirtualJoystick(this)

    // Keyboard controls for desktop
    if (this.input.keyboard) {
      this.cursors = this.input.keyboard.createCursorKeys()
      this.wasd = this.input.keyboard.addKeys({
        up: Phaser.Input.Keyboard.KeyCodes.W,
        down: Phaser.Input.Keyboard.KeyCodes.S,
        left: Phaser.Input.Keyboard.KeyCodes.A,
        right: Phaser.Input.Keyboard.KeyCodes.D
      }) as WASD

      // Keyboard attack (spacebar)
      this.input.keyboard.on('keydown-SPACE', () => {
        if (this.combatSystem.getTarget()) {
          this.combatSystem.attack()
        } else {
          // Auto-target closest if no target
          this.combatSystem.targetClosest()
        }
      })

      // Tab to cycle targets
      this.input.keyboard.on('keydown-TAB', (event: KeyboardEvent) => {
        event.preventDefault()
        this.combatSystem.targetClosest()
      })
    }
  }

  private setupCamera(): void {
    // Set camera to follow player
    this.cameras.main.startFollow(this.player, true, 0.1, 0.1)
    this.cameras.main.setZoom(1.5)

    // Set camera bounds to map size
    this.cameras.main.setBounds(
      0, 0,
      this.mapWidth * this.tileSize,
      this.mapHeight * this.tileSize
    )

    // Smooth deadzone
    this.cameras.main.setDeadzone(50, 50)
  }

  update(_time: number, delta: number): void {
    // Skip update if player is dead
    if (this.player.isDead) {
      // Still update some systems
      this.damageNumbers.update(delta)
      return
    }

    // Handle movement
    let velocityX = 0
    let velocityY = 0

    // Check keyboard input
    if (this.cursors && this.wasd) {
      if (this.cursors.left.isDown || this.wasd.left.isDown) {
        velocityX = -1
      } else if (this.cursors.right.isDown || this.wasd.right.isDown) {
        velocityX = 1
      }

      if (this.cursors.up.isDown || this.wasd.up.isDown) {
        velocityY = -1
      } else if (this.cursors.down.isDown || this.wasd.down.isDown) {
        velocityY = 1
      }
    }

    // Check joystick input (overrides keyboard if active)
    if (this.joystick.isActive) {
      const joyVel = this.joystick.getVelocity()
      velocityX = joyVel.x
      velocityY = joyVel.y
    }

    // Apply movement
    if (velocityX !== 0 || velocityY !== 0) {
      // Normalize diagonal movement
      const magnitude = Math.sqrt(velocityX * velocityX + velocityY * velocityY)
      if (magnitude > 1) {
        velocityX /= magnitude
        velocityY /= magnitude
      }
      this.player.move(velocityX, velocityY)
    } else {
      this.player.stopMoving()
    }

    // Update systems
    if (this.spawnsEnabled) {
      this.spawnSystem.update(delta)
    }
    this.combatSystem.update()
    this.statsSystem.update(delta)
    this.lootSystem.update()
    this.targetIndicator.update(delta)
    this.damageNumbers.update(delta)

    // Update decoration depth based on player position (Y-sorting)
    this.decorationLayer.getChildren().forEach(dec => {
      const sprite = dec as Phaser.GameObjects.Sprite
      sprite.setDepth(sprite.y)
    })
    this.player.setDepth(this.player.y)
  }

  shutdown(): void {
    // Clean up systems
    this.combatInput.destroy()
    this.targetIndicator.destroy()
    this.damageNumbers.destroy()
    this.combatSystem.destroy()
    this.statsSystem.destroy()
    this.lootSystem.destroy()
    this.inventorySystem.destroy()
    if (this.spawnSystem) {
      this.spawnSystem.destroy()
    }
  }
}
